import React, { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Center,
  Divider,
  Grid,
  Loader,
  MultiSelect,
  Select,
  Text,
} from "@mantine/core";
import { Link } from "react-router-dom";

import { DateRangePicker } from "@mantine/dates";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";

import { auth, db } from "../firebase";
import { getWeekOldDate } from "../components/listings/hooks";
import { fetchReportDownloadLinkCombined } from "../components/shared/hooks/generate-report";
import DownloadAlert from "../components/shared/downloadAlert";
import { PageHeader } from "../components/shared";

import {
  FuelReportConfig,
  CompanyCredits,
} from "@captiva/shared-types";
import { QuerySnapshot, DocumentData } from "firebase/firestore";

interface AssetDependencyConfig {
  assetType: string;
  key: string;
  label: string;
  assetLabelField: string;
  isArray: boolean;
}

interface ReportConfigExtended extends FuelReportConfig {
  id: string;
  assetDependencies?: AssetDependencyConfig[];
}

interface SelectedDependency {
  key: string;
  label: string;
  options: { value: string; label: string }[];
  isArray: boolean;
  selected: any; // string | string[] | null
  assetType: string;
}

export function Reports({ companyId }: { companyId: string }) {
  const [reportConfigs, setReportConfigs] = useState<ReportConfigExtended[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchedCredits, setFetchedCredits] = useState(false);
  const [creditDoc, setCreditDoc] = useState<CompanyCredits | null>(null);
  const [reportConfigsFetched, setReportConfigsFetched] = useState(false);
  const [selectedDateRange, setDates] = useState<[Date | null, Date | null]>([
    getWeekOldDate(1),
    new Date(),
  ]);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | undefined>();
  const [selectedDependencies, setSelectedDependencies] = useState<SelectedDependency[]>([]);
  const [fetchedDependencies, setFetchedDependencies] = useState<Record<string, any[]>>({});
  const [emptyReportWarning, setEmptyReportWarning] = useState<string | null>(null);

  useEffect(() => {
    // get reportconfig
    const selectedReportConfig = reportConfigs.find(
      (config) => config.id === selectedReportId
    );
    if (selectedReportConfig && selectedReportConfig.assetDependencies) {
      const assetDependencyConfigArray = selectedReportConfig.assetDependencies;

      assetDependencyConfigArray.forEach(async (assetDependencyConfig) => {
        // fetch all documents in assetDependencyConfig.assetType from firestore db (i.e. "fleet")
        const assetType = assetDependencyConfig.assetType;
        let fetchedAssets: any[] = [];

        if (fetchedDependencies[assetDependencyConfig.assetType]) {
          // no need to fetch again
          fetchedAssets = fetchedDependencies[assetDependencyConfig.assetType];
        } else {
          // fetch assets
          const querySnapshot = await getDocs(
            query(
              collection(db, assetType),
              where("companyId", "==", companyId)
            )
          );
          fetchedAssets = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setFetchedDependencies((prevState) => ({
            ...prevState,
            [assetType]: fetchedAssets,
          }));
        }

        setSelectedDependencies((prevState) => [
          ...prevState,
          {
            key: assetDependencyConfig.key,
            label: assetDependencyConfig.label,
            options: fetchedAssets.map((asset) => ({
              value: asset.id,
              label: asset[assetDependencyConfig.assetLabelField] || asset.id,
            })).sort((a, b) => (a.label || "").localeCompare(b.label || "")),
            isArray: assetDependencyConfig.isArray,
            selected: assetDependencyConfig.isArray ? [] : null,
            assetType: assetDependencyConfig.assetType,
          },
        ]);
      });
    } else {
      setSelectedDependencies([]);
    }
  }, [selectedReportId, reportConfigs]);

  useEffect(() => {
    if (!fetchedCredits && companyId) {
      const fetchCredits = async () => {
        setFetchedCredits(true);
        const docRef = doc(db, "credits", companyId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setCreditDoc(docSnap.data() as CompanyCredits);
        } else {
          // docSnap.data() will be undefined in this case
          console.error("No credit document");
        }
      };
      fetchCredits();
    }
  }, [companyId, fetchedCredits]);

  const buildQuery = (templateName: string) => {
    return query(
      collection(db, templateName),
      where("isActive", "==", true),
      where("isDateRanged", "==", true),
      where("isAdvancedReport", "==", false)
    );
  };

  useEffect(() => {
    const fetchConfigs = async () => {
      const documentSnapshots = await getDocs(buildQuery("reportSetUp"));

      handleResults(documentSnapshots, setReportConfigs);
    };

    const handleResults = (
      documentSnapshots: QuerySnapshot<DocumentData>,
      setter: React.Dispatch<React.SetStateAction<ReportConfigExtended[]>>
    ) => {
      let entries: ReportConfigExtended[] = [];

      documentSnapshots.forEach((doc) =>
        entries.push({ id: doc.id, ...doc.data() } as ReportConfigExtended)
      );

      entries.sort((a, b) => {
        if (a.reportShortName < b.reportShortName) {
          return -1;
        }
        if (a.reportShortName > b.reportShortName) {
          return 1;
        }
        return 0;
      });

      setter(entries);
    };

    try {
      if (!reportConfigsFetched) {
        setReportConfigsFetched(true);
        fetchConfigs();
      }
    } catch (error) {
      console.error(error);
    }
  }, [reportConfigsFetched]);

  const handleGenerateReport = async (
    reportId: string | null,
    dateRange: [Date | null, Date | null],
    selectedDependencies: SelectedDependency[]
  ) => {
    if (!reportId || !dateRange || !dateRange[0]) return;

    // Use toLocaleDateString('en-CA') to ensure YYYY-MM-DD format in local timezone
    const startDate = dateRange[0].toLocaleDateString("en-CA");
    // Handle single-day reports by defaulting endDate to startDate if not explicitly selected
    const endDate = (dateRange[1] || dateRange[0]).toLocaleDateString("en-CA");
    const reportToGenerate = reportConfigs.find((r) => r.id === reportId);
    if (!reportToGenerate) return;

    const reportRenderType = reportToGenerate.renderingType;
    const reportName =
      reportToGenerate.reportShortName +
      `-${new Date().toLocaleDateString("en-CA")}`;

    setLoading(true);
    setEmptyReportWarning(null);
    // Creating addlFieldMap based on selected dependencies
    let addlFieldMap: Record<string, any> | null = selectedDependencies.length ? {} : null;
    selectedDependencies.forEach((dep) => {
      if (addlFieldMap) {
        addlFieldMap[dep.key] = dep.selected;
      }
    });

    try {
      const result = await fetchReportDownloadLinkCombined(
        reportId,
        startDate,
        endDate,
        reportRenderType,
        reportName,
        companyId,
        companyId,
        auth.currentUser?.uid || "",
        addlFieldMap // directly pass addlFieldMap, it will be null if empty due to default parameter
      );

      if (result?.empty) {
        setEmptyReportWarning(result.message);
      } else {
        setDownloadUrl(result);
        setDownloadModalOpen(true);
      }
    } catch (error) {
      alert("Your report could not be generated. An error notification has been sent to the CaptivaData team for further investigation.");
    }

    setLoading(false);
  };

  return (
    <>
      <PageHeader
        title="Fuel Reports"
        subtitle="Date ranged"
        alert={
          (creditDoc?.creditBalance ?? 0) <= 0 ? (
            <Alert color="red" title="Credit Balance Low">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
                <Text size="sm">
                  Generating reports is disabled because your company has zero or negative credits remaining.
                </Text>
                <Link to="/dashboard/settings" style={{ textDecoration: "none" }}>
                  <Button color="red" size="sm" variant="outline">
                    Top Up Credits
                  </Button>
                </Link>
              </div>
            </Alert>
          ) : undefined
        }
      />
      <Box style={{ maxWidth: "500px", marginTop: "1em", margin: "auto" }}>
        <Select
          label="Choose a report to generate"
          placeholder="Report name"
          searchable
          value={selectedReportId}
          nothingFound="No matches found"
          onChange={v => {
            setSelectedReportId(v);
          }}
          data={reportConfigs.map((config) => {
            return { value: config.id, label: config.reportShortName };
          })}
        />
        {selectedDependencies.length > 0 &&
          selectedDependencies.map((dependency, i) => {
            return dependency.isArray ? (
              <MultiSelect
                style={{ marginTop: "1em" }}
                key={dependency.assetType}
                label={dependency.label}
                placeholder={dependency.label}
                value={dependency.selected}
                searchable
                nothingFound="No matches found"
                onChange={(values) => {
                  setSelectedDependencies((existingValues) => {
                    const updatedDependencies = existingValues.map(
                      (dep, index) =>
                        index === i ? { ...dep, selected: values } : dep
                    );
                    return [...updatedDependencies];
                  });
                }}
                data={dependency.options}
                required={true}
              />
            ) : (
              <Select
                style={{ marginTop: "1em" }}
                key={dependency.assetType}
                label={dependency.label}
                placeholder={dependency.label}
                value={dependency.selected}
                searchable
                nothingFound="No matches found"
                onChange={(value) => {
                  setSelectedDependencies((existingValues) => {
                    const updatedDependencies = existingValues.map(
                      (dep, index) =>
                        index === i ? { ...dep, selected: value } : dep
                    );
                    return [...updatedDependencies];
                  });
                }}
                data={dependency.options}
                required={true}
              />
            );
          })}
        <DateRangePicker
          label="Choose a date range"
          placeholder="Report date range"
          amountOfMonths={2}
          style={{ marginTop: "1em" }}
          clearable={false}
          value={selectedDateRange}
          onChange={setDates}
          allowSingleDateInRange
        />
        {loading ? (
          <Loader style={{ margin: "1em auto", width: "100%" }}></Loader>
        ) : (
          <>
            {emptyReportWarning && (
              <Alert
                color="yellow"
                title="No data found"
                withCloseButton
                onClose={() => setEmptyReportWarning(null)}
                style={{ marginTop: "1em" }}
              >
                {emptyReportWarning}
              </Alert>
            )}
            <Button
              color="cap-navy"
              size="md"
              style={{ margin: "2em 25%", width: "50%" }}
              disabled={
                !selectedDateRange ||
                !selectedDateRange[0] ||
                !selectedReportId ||
                (creditDoc?.creditBalance ?? 0) <= 0 ||
                selectedDependencies.some((dep) =>
                  dep.isArray ? dep.selected.length === 0 : !dep.selected
                )
              }
              onClick={() =>
                handleGenerateReport(
                  selectedReportId,
                  selectedDateRange,
                  selectedDependencies
                )
              }
            >
              Generate report
            </Button>
          </>
        )}
      </Box>
      <DownloadAlert
        url={downloadUrl}
        modalOpen={downloadModalOpen}
        onModalClose={() => setDownloadModalOpen(false)}
      ></DownloadAlert>
    </>
  );
}
